import Swal from "sweetalert2";
import { api } from "../../../api/Middleware";
import {
  SUBACCOUNTS_URL,
  SUBACCOUNTS_DETAIL_URL,
  SUBACCOUNTS_TOGGLE_STATUS_URL,
  SUBACCOUNTS_UPDATE_USER_LIMIT_URL,
  ORGANIZATIONS_ASSIGN_FEATURES_URL,
  USERS_URL,
  FEATURES_URL,
  VALIDATE_SUBACCOUNT_DATA_URL,
  CREATE_COMPLETE_SUBACCOUNT_URL,
} from "../../../api/Urls";

const SubaccountService = {
  // =====================================
  // OPERAÇÕES DE SUBCONTA
  // =====================================

  // Criar subconta
  createSubaccount: async (data) => {
    try {
      const response = await api.post(SUBACCOUNTS_URL, data);
      return response.data;
    } catch (error) {
      console.error("Error creating subaccount:", error);
      throw error;
    }
  },

  // Listar clientes
  getSubaccounts: async (params = {}) => {
    try {
      const response = await api.get(SUBACCOUNTS_URL, { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching subaccounts:", error);
      throw error;
    }
  },

  // Obter subconta específica
  getSubaccount: async (id) => {
    try {
      const response = await api.get(SUBACCOUNTS_DETAIL_URL(id));
      return response.data;
    } catch (error) {
      console.error("Error fetching subaccount:", error);
      throw error;
    }
  },

  // Atualizar subconta
  updateSubaccount: async (id, data) => {
    try {
      const response = await api.patch(SUBACCOUNTS_DETAIL_URL(id), data);
      return response.data;
    } catch (error) {
      console.error("Error updating subaccount:", error);
      throw error;
    }
  },

  // Ativar/desativar subconta
  toggleSubaccountStatus: async (id) => {
    try {
      const response = await api.post(SUBACCOUNTS_TOGGLE_STATUS_URL(id));
      return response.data;
    } catch (error) {
      console.error("Error toggling subaccount status:", error);
      throw error;
    }
  },

  // Atualizar limite de usuários
  updateUserLimit: async (id, maxUsers) => {
    try {
      const response = await api.post(SUBACCOUNTS_UPDATE_USER_LIMIT_URL(id), {
        max_users_allowed: maxUsers,
      });
      return response.data;
    } catch (error) {
      console.error("Error updating user limit:", error);
      throw error;
    }
  },

  // =====================================
  // OPERAÇÕES DE FEATURES
  // =====================================

  // Obter todas as features disponíveis
  getAvailableFeatures: async () => {
    try {
      const response = await api.get(FEATURES_URL);
      return response.data;
    } catch (error) {
      console.error("Error fetching features:", error);
      throw error;
    }
  },

  // Atribuir features à subconta
  assignFeatures: async (organizationId, data) => {
    try {
      const response = await api.post(
        ORGANIZATIONS_ASSIGN_FEATURES_URL(organizationId),
        data
      );
      return response.data;
    } catch (error) {
      console.error("Error assigning features:", error);
      throw error;
    }
  },

  // =====================================
  // OPERAÇÕES DE USUÁRIO
  // =====================================

  // Criar usuário admin da subconta
  createSubaccountAdmin: async (userData) => {
    try {
      const response = await api.post(USERS_URL, {
        ...userData,
        is_superuser: 1,
        superuser_level: 1,
        fromAdmin: true,
      });
      return response.data;
    } catch (error) {
      console.error("Error creating subaccount admin:", error);
      throw error;
    }
  },

  // Validar CPF
  validateCPF: async (cpf, email = "") => {
    try {
      const response = await api.post(`${USERS_URL}validate_cpf/`, {
        cpf,
        email,
      });
      return response.data;
    } catch (error) {
      if (error.response?.data && error.response.data?.error) {
        Swal.fire({
          title: "Oops!",
          html: error.response.data.error,
          icon: "error",
        });
        console.error("Error validating CPF:", error);
      } else {
        Swal.fire({
          title: "Oops!",
          html: "CPF inválido ou já cadastrado. Por favor tente novamente.",
          icon: "error",
        });
      }
      throw error;
    }
  },

  // Validar dados de subconta antes de criar (via API)
  validateSubaccountDataRemote: async (data) => {
    try {
      const response = await api.post(VALIDATE_SUBACCOUNT_DATA_URL, {
        subaccount: true,
        cpf_cnpj: data.cpf_cnpj,
        email: data.email,
        phone: data.phone || null,
        company_name: data.company_name || data.physical_name,
        account_type: 1,
      });
      
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error validating subaccount data:", error);
      
      let errorMessage = "Erro ao validar dados da subconta";
      let errorDetails = null;
      
      if (error.response?.data) {
        const serverError = error.response.data;
        
        if (serverError.error) {
          errorMessage = serverError.error;
        }
        
        if (serverError.details) {
          errorDetails = serverError.details;
        }
      }
      
      return {
        success: false,
        error: errorMessage,
        details: errorDetails,
        statusCode: error.response?.status || 500,
      };
    }
  },

  // =====================================
  // VALIDAÇÕES
  // =====================================

  // Validar dados da subconta
  validateSubaccountData: (data) => {
    const errors = {};

    // Validações obrigatórias
    if (!data.company_name || data.company_name.trim().length < 3) {
      errors.company_name = "Nome da empresa deve ter pelo menos 3 caracteres";
    }

    if (!data.email || !isValidEmail(data.email)) {
      errors.email = "Email inválido";
    }

    if (!data.cpf_cnpj || data.cpf_cnpj.length < 11) {
      errors.cpf_cnpj = "CPF/CNPJ inválido";
    }

    if (!data.business_segment) {
      errors.business_segment = "Segmento de negócio é obrigatório";
    }

    // Validar telefone se fornecido
    if (data.phone && !isValidPhone(data.phone)) {
      errors.phone = "Telefone inválido";
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  },

  // Validar dados do usuário admin
  validateAdminData: (data) => {
    const errors = {};

    if (!data.first_name || data.first_name.trim().length < 2) {
      errors.first_name = "Nome deve ter pelo menos 2 caracteres";
    }

    if (!data.phone || !isValidPhone(data.phone)) {
      errors.phone = "Telefone é obrigatório e deve ser válido";
    }

    if (!data.email || !isValidEmail(data.email)) {
      errors.email = "Email inválido";
    }

    if (data.cpf && !isValidCPF(data.cpf)) {
      errors.cpf = "CPF inválido";
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  },

  // Validar seleção de features
  validateFeatureSelection: (features, limits) => {
    const errors = {};

    // Verificar se pelo menos uma feature foi selecionada
    if (!features || features.length === 0) {
      errors.features = "Selecione pelo menos uma funcionalidade";
    }

    // Validar limites se fornecidos
    if (limits) {
      Object.keys(limits).forEach((featureId) => {
        const limit = limits[featureId];
        if (limit !== null && (isNaN(limit) || limit < 0)) {
          errors[`limit_${featureId}`] = "Limite deve ser um número positivo";
        }
      });
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  },

  // =====================================
  // OPERAÇÕES COMPLETAS
  // =====================================

  // Criar subconta completa usando endpoint atômico (account + admin + features)
  createCompleteSubaccount: async (
    subaccountData,
    adminData,
    featuresData,
    sendInvite = false
  ) => {
    try {
      // Preparar payload para o endpoint atômico
      const payload = {
        subaccount: {
          ...subaccountData,
          plan: "1", // Sempre empresa para clientes
          status: 1,
          terms_of_use: 1,
          subaccount: 1, // Backend espera 1, não boolean
        },
        admin: {
          ...adminData,
          is_superuser: 1,
          superuser_level: 1,
          is_active: 1,
          fromAdmin: true,
        },
        features: {
          feature_ids: featuresData.feature_ids || [],
          limits: featuresData.limits || {},
          notes: featuresData.notes || {},
        },
        send_invitation: sendInvite,
      };

      // Chamar endpoint atômico
      const response = await api.post(CREATE_COMPLETE_SUBACCOUNT_URL, payload);

      // Retornar no formato esperado pelo frontend
      return {
        account: response.data.account,
        admin: response.data.admin,
        features_assigned: response.data.features_assigned || 0,
        invitation_sent: response.data.invitation_sent || false,
      };
    } catch (error) {
      console.error("Error creating complete subaccount:", error);
      throw error;
    }
  },

  // =====================================
  // UTILITÁRIOS
  // =====================================

  // Agrupar features por categoria
  groupFeaturesByCategory: (features) => {
    const categoryMap = {
      agents: "Agentes de IA",
      service: "Atendimento",
      clients: "Clientes & Vendas",
      automations: "Automações",
      integrations: "Integrações",
      marketplace: "Marketplace",
      settings: "Configurações",
    };

    const grouped = {};
    features.forEach((feature) => {
      const category = feature.category || "others";
      const categoryName = categoryMap[category] || "Outros";

      if (!grouped[categoryName]) {
        grouped[categoryName] = [];
      }
      grouped[categoryName].push(feature);
    });

    return grouped;
  },

  // Formatar dados para envio
  formatSubaccountForSubmission: (formData) => {
    return {
      company_name: formData.company_name,
      email: formData.email,
      cpf_cnpj: formData.cpf_cnpj.replace(/\D/g, ""), // Remove caracteres não numéricos
      business_segment: formData.business_segment,
      name: formData.name || "",
      description: formData.description || "",
      phone: formData.phone?.replace(/\D/g, "") || "",
      company_type: formData.company_type || "LIMITED",
      person_type: formData.person_type || 1,
      title: formData.title || "",
      max_users_allowed: parseInt(formData.max_users_allowed) || 3,
      business_categories: formData.business_categories || [],
      social_media: formData.social_media || {},
    };
  },

  formatAdminForSubmission: (formData) => {
    return {
      first_name: formData.first_name,
      last_name: formData.last_name || "",
      email: formData.email,
      phone: formData.phone.replace(/\D/g, ""), // Remove caracteres não numéricos
      cpf: formData.cpf?.replace(/\D/g, "") || "",
      birthday: formData.birthday || null,
      mobile: formData.phone.replace(/\D/g, ""),
    };
  },
};

// =====================================
// FUNÇÕES AUXILIARES
// =====================================

function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

function isValidPhone(phone) {
  const cleanPhone = phone.replace(/\D/g, "");
  return cleanPhone.length >= 10 && cleanPhone.length <= 11;
}

function isValidCPF(cpf) {
  const cleanCPF = cpf.replace(/\D/g, "");
  if (cleanCPF.length !== 11) return false;

  // Verificar se todos os dígitos são iguais
  if (/^(\d)\1{10}$/.test(cleanCPF)) return false;

  // Validar dígitos verificadores
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (10 - i);
  }
  let digit1 = 11 - (sum % 11);
  if (digit1 > 9) digit1 = 0;

  if (parseInt(cleanCPF.charAt(9)) !== digit1) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (11 - i);
  }
  let digit2 = 11 - (sum % 11);
  if (digit2 > 9) digit2 = 0;

  return parseInt(cleanCPF.charAt(10)) === digit2;
}

export default SubaccountService;
